import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { storage } from '../utils/localStorage';

interface FiltersContextType {
    getFilter: (key: string) => string | null;
    setFilter: (key: string, value: string | null) => void;
    removeFilter: (key: string) => void;
    clearAllFilters: () => void;
    hasFilter: (key: string) => boolean;
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [filters, setFilters] = useState<Record<string, string>>(() => {
        const storedFilters: Record<string, string> = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('flowy_filter_')) {
                const filterKey = key.replace('flowy_filter_', '');
                const value = localStorage.getItem(key);
                if (value) {
                    storedFilters[filterKey] = value;
                }
            }
        }
        return storedFilters;
    });

    const getFilter = useCallback((key: string): string | null => {
        return filters[key] || null;
    }, [filters]);

    const setFilter = useCallback((key: string, value: string | null) => {
        // Empty/null → drop the filter entirely; we don't keep present-but-empty
        // state. Consumers query `hasFilter` to know if it's set.
        if (value === '' || value == null) {
            setFilters(prev => {
                if (!(key in prev)) return prev;
                const next = { ...prev };
                delete next[key];
                return next;
            });
            storage.removeItem(`filter_${key}`);
            return;
        }
        setFilters(prev => ({ ...prev, [key]: value }));
        storage.setItem(`filter_${key}`, value);
    }, []);

    const removeFilter = useCallback((key: string) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters[key];
            return newFilters;
        });
        storage.removeItem(`filter_${key}`);
    }, []);

    const clearAllFilters = useCallback(() => {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('flowy_filter_')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        setFilters({});
    }, []);

    const hasFilter = useCallback((key: string): boolean => {
        return key in filters;
    }, [filters]);

    // Hydrate context filters from the URL (e.g. an email deep-link):
    // ?filters[task_id]=389&filters[company_id]=3 → setFilter('task_id','389')…
    // URL context overrides any stored value for the same key.
    const [searchParams] = useSearchParams();
    useEffect(() => {
        searchParams.forEach((value, key) => {
            const match = key.match(/^filters\[(.+)\]$/);
            if (match && value) {
                setFilter(match[1], value);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    return (
        <FiltersContext.Provider value={{
            getFilter,
            setFilter,
            removeFilter,
            clearAllFilters,
            hasFilter
        }}>
            {children}
        </FiltersContext.Provider>
    );
};

export const useFilters = () => {
    const context = useContext(FiltersContext);
    if (!context) {
        throw new Error('useFilters must be used within FiltersProvider');
    }
    return context;
};